import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Language, strings, Strings } from '@/constants/strings'

interface LanguageContextType {
  language: Language
  setLanguage: (language: Language) => void
  t: Strings
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language from localStorage immediately to prevent flash
  const getInitialLanguage = (): Language => {
    try {
      const savedLanguage = localStorage.getItem('deepwind-language') as Language
      if (savedLanguage && (savedLanguage === 'tr' || savedLanguage === 'en')) {
        return savedLanguage
      }
      // Detect browser language
      const browserLanguage = navigator.language.toLowerCase()
      return browserLanguage.startsWith('tr') ? 'tr' : 'en'
    } catch {
      return 'tr' // Fallback
    }
  }

  const [language, setLanguageState] = useState<Language>(getInitialLanguage)

  // Listen to storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'deepwind-language' && e.newValue) {
        const newLanguage = e.newValue as Language
        if (newLanguage === 'tr' || newLanguage === 'en') {
          setLanguageState(newLanguage)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage)
    localStorage.setItem('deepwind-language', newLanguage)
  }

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: strings[language]
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export default LanguageProvider